import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router';
import UsersStore from '@apps/dashboard/src/store/usersStore';

const routes: Array<RouteRecordRaw> = [
	{
		path: '/',
		name: 'homepage',
		component: () =>
			import(
				/* webpackChunkName: "homepage " */ '@apps/dashboard/src/views/homePage/HomePage.vue'
			),
		meta: {
			title: 'homepage',
		},
	},
	{
		path: '/transactions',
		name: 'transactions',
		component: () =>
			import(/* webpackChunkName: "transactions" */ '@shared/views/TransactionsPage.vue'),
		meta: {
			title: 'transactions',
		},
	},
	{
		path: '/division',
		name: 'division',
		component: () =>
			import(/* webpackChunkName: "division" */ '@shared/components/division/Division.vue'),
		meta: {
			title: 'division',
		},
	},
	{
		path: '/reports',
		redirect: '/reports/division',
		name: 'reports',
		component: () =>
			import(
				/* webpackChunkName: "reports" */ '@apps/dashboard/src/views/reports/Reports.vue'
			),
		meta: {
			title: 'reports',
		},
		children: [
			{
				path: 'division',
				component: () => import('@apps/dashboard/src/views/reports/DivisionReports.vue'),
				props: true,
			},
			{
				path: 'sellers',
				component: () => import('@apps/dashboard/src/views/reports/SellerReports.vue'),
				props: true,
			},
		],
	},
	{
		path: '/sellers',
		name: 'sellers',
		component: () =>
			import(
				/* webpackChunkName: "sellers-page" */ '@apps/dashboard/src/views/sellers/Sellers.vue'
			),
		meta: {
			title: 'sellers',
		},
	},
	{
		path: '/sellers-details',
		name: 'sellerDetails',
		component: () =>
			import(
				/* webpackChunkName: "SellerDetails" */ '@shared/components/sellerDetails/SellerDetails.vue'
			),
		meta: {
			title: 'sellerDetails',
		},
	},
	{
		path: '/payouts',
		name: 'payouts',
		component: () =>
			import(
				/* webpackChunkName: "payouts" */ '@apps/dashboard/src/views/payouts/PayoutsPage.vue'
			),
		meta: {
			title: 'payouts',
		},
	},
	{
		path: '/payouts/:payoutRequestCode',
		name: 'payout-details',
		component: () =>
			import(
				/* webpackChunkName: "payout-request" */ '@shared/views/PayoutRequestDetailsPage.vue'
			),
		meta: {
			title: 'payoutDetails',
		},
	},
	{
		path: '/psp-settlements',
		name: 'psp-settlements',
		component: () =>
			import(/* webpackChunkName: "psp-settlements" */ '@shared/views/PspIntegration.vue'),
		meta: {
			title: 'pspSettlements',
		},
	},
	{
		path: '/balance-transfers',
		name: 'balance-transfers',
		component: () =>
			import(
				/* webpackChunkName: "balance-transfers" */ '@shared/components/balanceTransfer/balanceTransfers.vue'
			),
		meta: {
			title: 'balanceTransfers',
		},
	},
	{
		path: '/invoices',
		name: 'invoices',
		component: () =>
			import(
				/* webpackChunkName: "invoices" */ '@apps/dashboard/src/views/invoices/Invoices.vue'
			),
		meta: {
			title: 'invoices',
		},
	},
	{
		path: '/invoices/:invoiceId',
		name: 'invoice',
		component: () =>
			import(
				/* webpackChunkName: "invoice" */ '@apps/dashboard/src/views/invoices/InvoicePage.vue'
			),
		meta: {
			title: 'invoice',
		},
	},
	{
		path: '/contracts',
		name: 'contracts',
		component: () =>
			import(
				/* webpackChunkName: "contracts" */ '@apps/dashboard/src/views/contracts/Contracts.vue'
			),
		meta: {
			title: 'contracts',
		},
	},
	{
		path: '/configurations',
		redirect: '/configurations/overview',
		name: 'configurations',
		component: () =>
			import(
				/* webpackChunkName: "Configurations" */ '@shared/components/configuration/Configurations.vue'
			),
		meta: {
			title: 'configurations',
		},
		children: [
			{
				path: 'overview',
				component: () =>
					import(
						/* webpackChunkName: "OverviewConfigurations" */ '@shared/components/configuration/OverviewConfigurations.vue'
					),
			},
			{
				path: 'payouts',
				component: () =>
					import(
						/* webpackChunkName: "PayoutsConfiguration" */ '@shared/components/configuration/PayoutsConfiguration.vue'
					),
				children: [
					{
						path: 'scheduler/:type',
						component: () =>
							import(
								/* webpackChunkName: "PayoutSchedulerConfiguration" */ '@shared/components/configuration/PayoutSchedulerConfiguration.vue'
							),
					},
				],
			},
			{
				path: 'webhooks',
				component: () =>
					import(
						/* webpackChunkName: "WebhooksConfiguration" */ '@shared/components/configuration/WebhooksConfiguration.vue'
					),
			},
			{
				path: 'hosted-onboarding',
				component: () =>
					import(
						/* webpackChunkName: "HostedOnboardingConfiguration" */ '@shared/components/configuration/HostedOnboardingConfiguration.vue'
					),
			},
		],
	},
	{
		path: '/:pathMatch(.*)*',
		name: 'notFound',
		component: () =>
			import(/* webpackChunkName: "transactions" */ '@apps/dashboard/src/views/NotFound.vue'),
		meta: {
			title: 'notFound',
		},
	},
];

const router = createRouter({
	history: createWebHistory(process.env.BASE_URL),
	routes,
});

router.beforeEach((to, from, next) => {
	document.title = `Payaut | ${to.meta.title}`;

	/**
	 * Homepage is showing division balances
	 * this is a very expensive operation that
	 * times-out for larger merchants
	 * As a temp solution until bookkeeping 2.0
	 * we are showing this page conditionally
	 */
	const { user } = UsersStore();
	if (to.path === '/' && !user.hasAccessToHomepage) {
		next({ name: 'sellers' });
	} else if (to.meta.env && to.meta.env !== process.env.VUE_APP_ENV) {
		next({ path: '/pageNotFound' });
	} else {
		/**
		 * After keycloak validation redirect url has
		 * a hash with some info after it. We need to
		 * sanitize url on every refresh.
		 */
		[to.fullPath] = to.fullPath.split('#');

		next();
	}
});

export default router;
